import { Pipe, PipeTransform } from '@angular/core';
import { marked, Renderer, Tokens } from 'marked';
import { environment } from 'src/environments/environment';

@Pipe({
  name: 'markdown',
  pure: true
})
export class MarkdownPipe implements PipeTransform {

  // Backend base URL — in dev the Angular CLI runs on a different port than Express
  private backendBase = typeof window !== 'undefined' && window.location.port
    ? `${window.location.protocol}//${window.location.hostname}:8080`
    : '';

  constructor() {
    // Configure marked once
    const self = this;
    const renderer = new Renderer();

    // Safer <a> that disallows javascript: etc.
    renderer.link = ({ href, title, text }: { href: string; title?: string; text: string }) => {
        const safeHref = self.safeUrl(href);
        const titleAttr = title ? ` title="${self.escapeHtml(title)}"` : '';
        return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
    };

    // Route /data/image/ URLs through the backend
    renderer.image = ({ href, title, text }: { href: string; title?: string; text: string }) => {
        let src = href || '';
        if (src.startsWith('/data/image/')) {
          src = self.backendBase + src;
        }
        const alt = self.escapeHtml(text || '');
        const titleAttr = title ? ` title="${self.escapeHtml(title)}"` : '';
        return `<img src="${src}" alt="${alt}"${titleAttr}>`;
    };

    renderer.table = function (token: Tokens.Table): string {
      const headerCells = token.header.map((cell) => this.tablecell(cell)).join('');
      const header = `<thead>${this.tablerow({ text: headerCells })}</thead>`;
      const rows = token.rows
        .map((row) => this.tablerow({ text: row.map((cell) => this.tablecell(cell)).join('') }))
        .join('');
      const body = rows ? `<tbody>${rows}</tbody>` : '';

      return `<div class="chat-table-wrapper"><table class="chat-response-table">${header}${body}</table></div>`;
    };

    renderer.tablecell = function (token: Tokens.TableCell): string {
      const tag = token.header ? 'th' : 'td';
      const alignAttr = token.align ? ` style="text-align:${token.align}"` : '';
      const content = this.parser.parseInline(token.tokens);

      return `<${tag}${alignAttr}><div class="chat-cell-content">${content}</div></${tag}>`;
    };

    marked.use({
      renderer,
      gfm: true,
      breaks: true
    });
  }

  // Precompute storage blob rewrite pattern once
  private storageBlobPattern: RegExp | null = environment.storageBlobHost
    ? new RegExp(
        'https?://' + environment.storageBlobHost.replace(/\./g, '\\.') + '/([^?\\s")\\]]+)(\\?[^"\\s)\\]]*)?',
        'g'
      )
    : null;

  transform(value: string | null | undefined): string {
    if (!value) return '';
    // Rewrite storage blob URLs to proxy path (covers both new and old saved messages)
    const rewritten = this.storageBlobPattern
      ? value.replace(this.storageBlobPattern, '/data/image/$1')
      : value;
    // marked.parse is synchronous when no async plugins are used
    return marked.parse(rewritten) as string;
  }

  // --- helpers ---
  private safeUrl(url: string | undefined): string {
    const u = (url || '').trim();
    // allow only http(s), mailto, tel; everything else becomes a harmless '#'
    if (/^(https?:|mailto:|tel:)/i.test(u)) return u;
    return '#';
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
